import { useEffect, useRef, useState, useTransition } from 'react'
import { useHistory, useParams } from 'react-router'
import publisherApi from 'src/component/app/publisher/PublisherApi'
import { DspBean, LanguageBean, PublisherBean, TimeZoneBean, CountryBean } from '../../../api/api'
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader'
import { Alert, Button, Checkbox, Dropdown, Form, Input, InputNumber, Menu, Modal, Radio, Select, Switch, Table, TreeSelect, Upload, notification, Segmented } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import adTagJsScript from '../../../adTagJsScript';
import { env } from '../../../env';
import { RevenueCalculationMethod, SellerType, getIabCategoriesAsTree, resolveIabDefaultsChange, stripSelectDefaultsValue, truncateErrorMessage } from '../../../util/Util';
import PublisherTabs, { PublisherTabItem } from './PublisherTabs';
import { BasicDetails, FilteringOptions, PaymentDetails } from './PublisherTabSections';
import { ArrowLeftOutlined, CheckCircleOutlined, CloseOutlined, EyeOutlined, PlusOutlined, StopOutlined, UploadOutlined } from '@ant-design/icons';
import { StorageKey, storageReaderFactory, storageWriterFactory } from '../../../util/Storage'
import ChangeLogHistoryModal from '../changeHistory/changeLogHistoryModal'
import { PUBLISHER_EXCLUDED_FIELDS, PUBLISHER_FIELD_LABELS } from '../../../util/changeLogFieldLabels'
import PublisherDomainListCard from './PublisherDomainListCard'

const FormItem = Form.Item
const Option = Select.Option

const formInfoItems: FormDataType[] = [
    FormDataType.DspList,
    FormDataType.IabCategories,
    FormDataType.Languages,
    FormDataType.TimeZones,
    FormDataType.Publishers,
    FormDataType.Countries
]

const WinNotificationEnum = DspBean.WinNotificationEnum
type PublisherEditTab = 'basic' | 'filtering' | 'payment'
const publisherTabs: PublisherTabItem[] = [
    { key: 'basic', label: 'Basic Details' },
    { key: 'filtering', label: 'Filtering Options' },
    { key: 'payment', label: 'Payment Details' },
]
type PaymentCustomField = {
    id: string
    label: string
    placeholder?: string
}

export const AD_SERVER_URL = 'AD_SERVER_URL'
export const PUBLISHER_TOKEN = 'PUBLISHER_TOKEN'

const ADFI_REQUIRED_FIELD_NAMES = [
    'adfiFeePercent',
    'serviceFeePercent',
    'publisherLegalName',
    'publisherRegisteredAddress',
    'publisherContactEmail',
    'currencyPreference',
    'vatRate',
    'bankAccountName',
    'bankAccountType',
    'bankAccountNumber',
    'sortCode',
    'iban',
    'swiftBicCode',
    'routingNumber',
    'paymentTermsDays',
] as const

const PAYMENT_TAB_FIELDS = new Set([
    ...ADFI_REQUIRED_FIELD_NAMES,
    'vatRegistrationNumber',
    'customPaymentFields',
])

const PublisherEdit = () => {

    const [info, setInfo] = useState({
        dspList: [],
        iabCategories: [],
        languages: [],
        timezones: [],
        publishers: [],
        countries: [],
    })

    const history = useHistory()
    const { id } = useParams<{ id?: string }>()
    const publisherId = Number(id);

    const [token, setToken] = useState(undefined);
    const [error, setError] = useState(undefined);
    const [validation, setValidation] = useState(false);
    const [formLoaded, setFormLoaded] = useState(false);
    const [useParentSellerAccountId, setuseParentSellerAccountId] = useState(null);
    const [parentPublisherId, setParentPublisherId] = useState(undefined);
    const [passThrough, setpassThrough] = useState(false);
    const [includeDomains, setIncludeDomains] = useState(false);
    const [form] = Form.useForm();
    const [saveMode, setSaveMode] = useState<'DRAFT' | 'SAVE'>('SAVE');
    const saveModeRef = useRef<'DRAFT' | 'SAVE'>('SAVE')
    const iabPrevRef = useRef<number[]>([])
    const [status, setStatus] = useState<string>();
    const [activeTab, setActiveTab] = useState<PublisherEditTab>('basic')
    const [adfiEligible, setAdfiEligible] = useState(false)
    const [isCustomFieldModalOpen, setIsCustomFieldModalOpen] = useState(false)
    const [customPaymentFields, setCustomPaymentFields] = useState<PaymentCustomField[]>([])
    const [customFieldForm] = Form.useForm()
    const [isChangeLogOpen, setIsChangeLogOpen] = useState(false)

    const [allClassification, setAllClassification] = useState(false)
    const [acceptAllMobileAppIds, setAcceptAllMobileAppIds] = useState(false)
    const [domainMobileError, setDomainMobileError] = useState('')

    // ─── Domains state ──────────────────────────────────────────────────────
    const [domains, setDomains] = useState<string[]>([])
    const [domainsCollapsed, setDomainsCollapsed] = useState(false)
    const [domainsFileLabel, setDomainsFileLabel] = useState<string | null>(null)

    // ─── Mobile App IDs state ───────────────────────────────────────────────
    const [mobileAppIds, setMobileAppIds] = useState<string[]>([])
    const [mobileAppsCollapsed, setMobileAppsCollapsed] = useState(false)
    const [mobileAppIdsFileLabel, setMobileAppIdsFileLabel] = useState<string | null>(null)

    const navigateToPublisherList = () => {
        history.push('/publishers', { fromPublisherList: true });
    };

    const backPath = `/publishers`;

    const validateDomainsOrMobileApps = (): boolean => {
        if (allClassification || acceptAllMobileAppIds) {
            setDomainMobileError('')
            return true
        }
        if (domains.length === 0 && mobileAppIds.length === 0) {
            setDomainMobileError('Either Domains or Mobile App IDs is required')
            setActiveTab('filtering')
            return false
        }
        setDomainMobileError('')
        return true
    }

    const handleSubmit = () => {
        const mode = saveModeRef.current

        // 🟡 SAVE AS DRAFT → validate name + adfi fields if adfiEligible
        if (mode === 'DRAFT') {
          const fieldsToValidate = adfiEligible ? ['name', ...adfiRequiredFieldNames] : ['name']
          form
            .validateFields(fieldsToValidate)
            .then(() => {
              const allValues = form.getFieldsValue(true);
              submitPublisher(allValues, true, false);
            })
            .catch((err) => {
              const firstField = err?.errorFields?.[0]?.name
              const firstKey = Array.isArray(firstField) ? String(firstField[0]) : String(firstField)
              if (new Set(adfiRequiredFieldNames).has(firstKey)) {
                setActiveTab('payment')
              } else {
                setActiveTab('basic')
              }
              setTimeout(() => form.scrollToField(firstField, { behavior: 'smooth', block: 'center' }), 100)
            });
          return;
        }

        // 🟢 DRAFT → ACTIVATE — form validation first, then domain check
        if (status === 'DRAFT') {
            form
            .validateFields()
            .then(() => {
              if (!validateDomainsOrMobileApps()) return;
              const allValues = form.getFieldsValue(true);
              submitPublisher(allValues, false, true);
            })
            .catch((err) => {
              const firstField = err?.errorFields?.[0]?.name
              const firstKey = Array.isArray(firstField) ? String(firstField[0]) : String(firstField)
              if (firstKey === 'countries' || firstKey === 'iabCategoriesIds' || firstKey === 'dspBlackListIds') {
                setActiveTab('filtering')
                setTimeout(() => form.scrollToField(firstField, { behavior: 'smooth', block: 'center' }), 100)
                return
              }
              if (PAYMENT_TAB_FIELDS.has(firstKey)) {
                if (!validateDomainsOrMobileApps()) return
                setActiveTab('payment')
              } else {
                setActiveTab('basic')
              }
              setTimeout(() => form.scrollToField(firstField, { behavior: 'smooth', block: 'center' }), 100)
            });
            return;
        }

        // 🟢 ACTIVE or DEACTIVATED → SAVE only
        form
            .validateFields()
            .then(() => {
              if (!validateDomainsOrMobileApps()) return;
              const allValues = form.getFieldsValue(true);
              submitPublisher(allValues, false, false);
            })
            .catch((err) => {
              const firstField = err?.errorFields?.[0]?.name
              const firstKey = Array.isArray(firstField) ? String(firstField[0]) : String(firstField)
              if (firstKey === 'countries' || firstKey === 'iabCategoriesIds' || firstKey === 'dspBlackListIds') {
                setActiveTab('filtering')
                setTimeout(() => form.scrollToField(firstField, { behavior: 'smooth', block: 'center' }), 100)
                return
              }
              if (PAYMENT_TAB_FIELDS.has(firstKey)) {
                if (!validateDomainsOrMobileApps()) return
                setActiveTab('payment')
              } else {
                setActiveTab('basic')
              }
              setTimeout(() => form.scrollToField(firstField, { behavior: 'smooth', block: 'center' }), 100)
            });
    };

    const submitPublisher = async (values: any, isDraft: boolean, shouldActivate: boolean) => {
        const domainList: string[] = allClassification ? [] : domains
        const mobileAppIdList: string[] = acceptAllMobileAppIds ? [] : mobileAppIds
        const countries = values?.countries?.map((countryId: number) => ({ id: countryId })) || [];
        delete values.blackListDomainsField;
        delete values.domains;

        // Build payment details payload
        const isAdFiEligible = values?.adfiEligible === undefined ? false : Boolean(values?.adfiEligible)
        const paymentDetails = {
            legalName: values?.publisherLegalName,
            registeredAddress: values?.publisherRegisteredAddress,
            contactEmail: values?.publisherContactEmail,
            currencyPreference: values?.currencyPreference,
            vatRegistrationNumber: values?.vatRegistrationNumber,
            vatRate: values?.vatRate,
            bankAccountName: values?.bankAccountName,
            bankAccountNumber: values?.bankAccountNumber,
            bankAccountType: values?.bankAccountType,
            sortCode: values?.sortCode,
            iban: values?.iban,
            routingNumber: values?.routingNumber,
            swiftBicCode: values?.swiftBicCode,
            paymentTermsDays: values?.paymentTermsDays,
            isAdFiEligible,
        }

        const publisherPayload = { ...values }
        publisherPayload.adFiEnabled = Boolean(values?.adfiEligible)
        delete publisherPayload.adfiEligible
        publisherPayload.adFiFee = values?.adfiFeePercent
        publisherPayload.serviceFee = values?.serviceFeePercent
        delete publisherPayload.adfiFeePercent
        delete publisherPayload.serviceFeePercent
        delete publisherPayload.publisherLegalName
        delete publisherPayload.publisherRegisteredAddress
        delete publisherPayload.publisherContactEmail
        delete publisherPayload.currencyPreference
        delete publisherPayload.vatRate
        delete publisherPayload.vatRegistrationNumber
        delete publisherPayload.bankAccountName
        delete publisherPayload.bankAccountType
        delete publisherPayload.bankAccountNumber
        delete publisherPayload.sortCode
        delete publisherPayload.iban
        delete publisherPayload.swiftBicCode
        delete publisherPayload.routingNumber
        delete publisherPayload.paymentTermsDays
        delete publisherPayload.customPaymentFields

        try {
            // Persist payment details only when AdFI is eligible (toggle enabled).
            // When AdFI is disabled, the payment-details endpoint must not be hit.
            if (isAdFiEligible) {
                await publisherApi.updatePaymentDetails(Number(id), paymentDetails)
            }

            await publisherApi.edit(id, {
                ...publisherPayload,
                iabCategoriesIds: stripSelectDefaultsValue(values.iabCategoriesIds),
                domains: domainList,
                acceptAllDomains: allClassification,
                mobileAppIds: mobileAppIdList,
                acceptAllMobileAppIds,
                countries,
                validation: validation || false,
                useParentSellerAccountId,
                passThrough,
                ...(isDraft && { status: 'DRAFT' }),
            })

            if (isDraft) {
                Modal.info({
                    title: 'Publisher saved as Draft',
                    content: 'This publisher is in Draft status. Activate it to enable bidding and generate API credentials.',
                    onOk: () => navigateToPublisherList(),
                });
                return;
            }

            if (shouldActivate) {
                activatePublisher();
                return;
            }

            Modal.success({
                title: 'Publisher updated',
                onOk: () => navigateToPublisherList(),
            });
        } catch (err: any) {
            if (err?.error) {
                setError(truncateErrorMessage(err?.error))
            } else {
                Modal.error({
                    title: 'Update Failed',
                    content: err?.error || 'Unable to update publisher',
                })
            }
        }
    };

    const activatePublisher = async () => {
        try {
            const response = await publisherApi.activate(Number(id))
            if (response?.data?.authDataBean) {
                userCredentials(response.data.authDataBean);
            } else {
                navigateToPublisherList();
            }
        } catch (err: any) {
            Modal.error({
                title: 'Activation Failed',
                content: err?.error || 'Unable to activate publisher',
            })
        }
    };

    const userCredentials = (authDataBean) => {
        Modal.info({
            title: 'New account created',
            content: <div>
                <br />
                <div>Please save account credentials</div>
                <br />
                <div>Username: {authDataBean.username}</div>
                <div>Password: {authDataBean.password}</div>
            </div>,
            okText: 'OK',
            okCancel: false,
            onOk: () => navigateToPublisherList()
        });
    };

    const onRejected = err => {
        setError(truncateErrorMessage(err?.error))
    }

    const loadData = async () => {
        try {
            let response: any = await publisherApi.view(id)
            setuseParentSellerAccountId(response?.data?.useParentSellerAccountId)
            setpassThrough(response?.data?.passThrough)
            setIncludeDomains(response?.data?.includeDomains)

            if (response?.data?.acceptAllDomains) {
                setAllClassification(true)
            }
            if (response?.data?.acceptAllMobileAppIds) {
                setAcceptAllMobileAppIds(true)
            }

            // Pre-populate domains
            if (Array.isArray(response.data?.domains) && response.data.domains.length > 0) {
                const loadedDomains = response.data.domains
                setDomains(loadedDomains)
                setDomainsFileLabel('domains.csv')
            }

            // Pre-populate mobileAppIds
            if (Array.isArray(response.data?.mobileAppIds) && response.data.mobileAppIds.length > 0) {
                const loadedMobileAppIds = response.data.mobileAppIds
                setMobileAppIds(loadedMobileAppIds)
                setMobileAppIdsFileLabel('mobileAppIds.csv')
            }

            // Fetch payment details separately
            let paymentDetailsData: any = null
            try {
                const paymentDetailsResp: any = await publisherApi.getPaymentDetails(Number(id))
                paymentDetailsData = paymentDetailsResp?.data ?? paymentDetailsResp
            } catch (err) {
                console.error('Failed to load payment details', err)
            }

            let formInfo = await formDataLoader(...formInfoItems)
            setInfo(formInfo as any)

            const isAdFiEligibleValue =
                paymentDetailsData?.isAdFiEligible ??
                paymentDetailsData?.adfiEligible ??
                paymentDetailsData?.adFiEnabled ??
                response?.data?.isAdFiEligible ??
                response?.data?.adfiEligible ??
                response?.data?.adFiEnabled
            setAdfiEligible(Boolean(isAdFiEligibleValue))

            const savedCustom =
                paymentDetailsData?.customPaymentFields ??
                response?.data?.customPaymentFields ??
                {}
            if (Array.isArray(savedCustom)) {
                setCustomPaymentFields(savedCustom.map((item: any, index: number) => ({
                    id: item?.id || `saved-${index}`,
                    label: item?.label || `Custom Field ${index + 1}`,
                    placeholder: item?.placeholder || '',
                })))
            } else if (savedCustom && typeof savedCustom === 'object') {
                setCustomPaymentFields(Object.keys(savedCustom).map((key) => ({
                    id: key,
                    label: key,
                    placeholder: '',
                })))
            }

            const formData = {
                ...response.data,
                countries: response.data.countries?.map((country: CountryBean) => country.id) || [],
            }

            // Pre-fill Payment Details tab fields from `/payment-details` response
            if (paymentDetailsData) {
                if (isAdFiEligibleValue !== undefined) formData.adfiEligible = Boolean(isAdFiEligibleValue)
                if (paymentDetailsData.legalName !== undefined) formData.publisherLegalName = paymentDetailsData.legalName
                if (paymentDetailsData.registeredAddress !== undefined) formData.publisherRegisteredAddress = paymentDetailsData.registeredAddress
                if (paymentDetailsData.contactEmail !== undefined) formData.publisherContactEmail = paymentDetailsData.contactEmail
                if (paymentDetailsData.currencyPreference !== undefined) formData.currencyPreference = paymentDetailsData.currencyPreference
                if (paymentDetailsData.vatRegistrationNumber !== undefined) formData.vatRegistrationNumber = paymentDetailsData.vatRegistrationNumber
                if (paymentDetailsData.vatRate !== undefined) formData.vatRate = paymentDetailsData.vatRate
                if (paymentDetailsData.bankAccountName !== undefined) formData.bankAccountName = paymentDetailsData.bankAccountName
                if (paymentDetailsData.bankAccountNumber !== undefined) formData.bankAccountNumber = paymentDetailsData.bankAccountNumber
                if (paymentDetailsData.bankAccountType !== undefined) formData.bankAccountType = paymentDetailsData.bankAccountType
                if (paymentDetailsData.routingNumber !== undefined) formData.routingNumber = paymentDetailsData.routingNumber
                if (paymentDetailsData.swiftBicCode !== undefined) formData.swiftBicCode = paymentDetailsData.swiftBicCode
                if (paymentDetailsData.paymentTermsDays !== undefined) formData.paymentTermsDays = paymentDetailsData.paymentTermsDays
                if (paymentDetailsData.adFiFee !== undefined) formData.adfiFeePercent = paymentDetailsData.adFiFee
                if (paymentDetailsData.serviceFee !== undefined) formData.serviceFeePercent = paymentDetailsData.serviceFee
                if (paymentDetailsData.adfiFee !== undefined && formData.adfiFeePercent === undefined) formData.adfiFeePercent = paymentDetailsData.adfiFee
                if (paymentDetailsData.servicefee !== undefined && formData.serviceFeePercent === undefined) formData.serviceFeePercent = paymentDetailsData.servicefee
                if (paymentDetailsData.sortCode !== undefined) formData.sortCode = paymentDetailsData.sortCode
                if (paymentDetailsData.iban !== undefined) formData.iban = paymentDetailsData.iban
                if (paymentDetailsData.customPaymentFields !== undefined) formData.customPaymentFields = paymentDetailsData.customPaymentFields
            } else {
                if (formData.adfiEligible === undefined && isAdFiEligibleValue !== undefined) {
                    formData.adfiEligible = Boolean(isAdFiEligibleValue)
                }
            }

            const adFiFeeValue =
                paymentDetailsData?.adFiFee ??
                paymentDetailsData?.adfiFee ??
                response?.data?.adFiFee ??
                response?.data?.adfiFee ??
                response?.data?.adfiFeePercent
            if (adFiFeeValue !== undefined) formData.adfiFeePercent = adFiFeeValue

            const serviceFeeValue =
                paymentDetailsData?.serviceFee ??
                paymentDetailsData?.servicefee ??
                response?.data?.serviceFee ??
                response?.data?.servicefee ??
                response?.data?.serviceFeePercent
            if (serviceFeeValue !== undefined) formData.serviceFeePercent = serviceFeeValue

            delete formData.domainCategories;
            delete formData.mobileAppIds;

            form.setFieldsValue(formData)
            setStatus(response?.data?.status);
            if (response?.data?.parentPublisherId) {
                setParentPublisherId(response?.data?.parentPublisherId)
            }
            setToken(response.data.token)
            setFormLoaded(true)
        } catch (e) {
            onRejected(e)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const handleTokenGenerator = () => {
        Modal.confirm({
            title: 'Generate new token',
            content: 'Do you want generate new publisher token?',
            okText: 'Yes',
            cancelText: 'NO',
            onOk: async () => {
                try {
                    let response: any = await publisherApi.generateToken(Number.parseInt(id ?? '-1'))
                    setToken(response.data.token)
                } catch (e) {
                    onRejected(e)
                }
            }
        });
    };

    const handleSelectAll = (value: any) => {
        if (value && value.length && value.includes('__all')) {
            if (value.length === info.dspList.length + 1) {
                return []
            }
            return [...(info.dspList.map((el: DspBean) => el.id))]
        }
        return value
    }

    const handleValidation = (value) => { setValidation(value.target.checked) };
    const handleuseParentSellerAccountId = (value) => { setuseParentSellerAccountId(value.target.checked) };
    const handleParentPublisherId = (event) => { setParentPublisherId(event) }
    const handlepassThrough = (value) => { setpassThrough(value.target.checked) };
    const handleIncludeDomains = (value) => { setIncludeDomains(value.target.checked) };

    const adfiRequiredFieldNames = ADFI_REQUIRED_FIELD_NAMES

    const getAdfiRequiredRule = (fieldLabel: string) =>
        adfiEligible ? [{ required: true, message: `Please input ${fieldLabel}` }] : []

    const handleAdfiEligibleChange = (checked: boolean) => { setAdfiEligible(checked) }

    const clearAdfiFieldErrors = () => {
        form.setFields(adfiRequiredFieldNames.map((name) => ({ name, errors: [] })))
    }

    const handleOpenCustomFieldModal = () => {
        customFieldForm.resetFields()
        setIsCustomFieldModalOpen(true)
    }

    const handleAddCustomField = () => {
        customFieldForm
            .validateFields()
            .then((values) => {
                const label = (values.fieldLabel || '').trim()
                const placeholder = (values.placeholder || '').trim()
                const fieldId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
                setCustomPaymentFields((prev) => [...prev, { id: fieldId, label, placeholder }])
                setIsCustomFieldModalOpen(false)
                customFieldForm.resetFields()
            })
            .catch(() => { })
    }

    const handleRemoveCustomField = (fieldId: string) => {
        setCustomPaymentFields((prev) => prev.filter((field) => field.id !== fieldId))
        form.setFieldsValue({
            customPaymentFields: {
                ...(form.getFieldValue('customPaymentFields') || {}),
                [fieldId]: undefined,
            },
        })
    }

    const generateCode = () => {
        Modal.info({
            width: 1024,
            title: 'Paste this code on your site:',
            content: <TextArea rows={30} value={scriptGenerator()} style={{ fontSize: 13 }} />,
            okText: 'Close',
        });
    }

    const scriptGenerator = () => replaceTagVars(adTagJsScript)

    const replaceTagVars = (scriptBody) => scriptBody
        .replace(AD_SERVER_URL, env.adServerUrl)
        .replace(PUBLISHER_TOKEN, token)

    const onChangeIab = (value: number[]) => {
        const next = resolveIabDefaultsChange(value || [], iabPrevRef.current, info.iabCategories as any)
        iabPrevRef.current = next
        form.setFieldsValue({ ['iabCategoriesIds']: next })
        return next
    }

    const handleDownload = async () => {
        try {
            const textContent = await publisherApi.downloadTxtFile(publisherId);
            if (!textContent) { console.log('No content received'); return; }
            const blob = new Blob([textContent], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `publisher_${publisherId}.ads.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const handleDownloadAppAds = async () => {
        try {
            const textContent = await publisherApi.downloadAppAdsTxtFile(publisherId);
            if (!textContent) { console.log('No content received'); return; }
            const blob = new Blob([textContent], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `publisher_${publisherId}.app-ads.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('Error downloading app-ads file:', error);
            const message = error?.error || error?.response?.data?.message || error?.message || '500 Internal Server Error';
            notification.error({ message: 'Download Failed', description: message, duration: 5, placement: 'top', style: { width: 600 } });
        }
    };

    const handleChangeLogHistory = () => { setIsChangeLogOpen(true) }

    useEffect(() => {
        if (!useParentSellerAccountId) {
            form.setFields([{ name: "parentPublisherId", errors: [] }]);
        }
    }, [useParentSellerAccountId]);

    useEffect(() => {
        if (!adfiEligible) {
            clearAdfiFieldErrors()
        }
    }, [adfiEligible])

    // ─── Domain handlers ────────────────────────────────────────────────────
    const handleAddDomain = (value: string) => setDomains(prev => [...prev, value])
    const handleUploadDomains = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setDomains(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }
    const handleRemoveDomain = (index: number) =>
        setDomains(prev => prev.filter((_, i) => i !== index))
    const handleClearAllDomains = () => setDomains([])

    // ─── Mobile App ID handlers ─────────────────────────────────────────────
    const handleAddMobileAppId = (value: string) => setMobileAppIds(prev => [...prev, value])
    const handleUploadMobileAppIds = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setMobileAppIds(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }
    const handleRemoveMobileAppId = (index: number) =>
        setMobileAppIds(prev => prev.filter((_, i) => i !== index))
    const handleClearAllMobileAppIds = () => setMobileAppIds([])

    const isBasicTab = activeTab === 'basic'
    const isFilteringTab = activeTab === 'filtering'
    const isPaymentTab = activeTab === 'payment'
    const isDraftStatus = status === 'DRAFT'

    const actionsMenu = (
        <Menu selectable={false}>
            <Menu.Item key="generate-js-tag" onClick={generateCode}>Generate JS Tag</Menu.Item>
            <Menu.Item key="change-log-history" onClick={handleChangeLogHistory}>Change Log History</Menu.Item>
            <Menu.Item key="download-app-ads" onClick={handleDownloadAppAds}>Download App-Ads.txt</Menu.Item>
            <Menu.Item key="download-ads" onClick={handleDownload}>Download Ads.txt</Menu.Item>
        </Menu>
    )

    return (
        <div>
            {error && <div><Alert message={error} type="error" /><br /></div>}
            <ChangeLogHistoryModal
                entityType="PUBLISHER"
                entityId={publisherId}
                open={isChangeLogOpen}
                onClose={() => setIsChangeLogOpen(false)}
                fieldLabelMap={PUBLISHER_FIELD_LABELS}
                excludeFields={PUBLISHER_EXCLUDED_FIELDS}
            />
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                }}
            >
                <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigateToPublisherList()}>
                    Go Back
                </Button>
                <PublisherTabs
                    tabs={publisherTabs}
                    activeTab={activeTab}
                    onTabChange={(value) => setActiveTab(value as PublisherEditTab)}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                    <Dropdown overlay={actionsMenu} trigger={['click']} placement="bottomLeft">
                        <Button type="default">Actions</Button>
                    </Dropdown>
                    {isDraftStatus ? (
                        <>
                            <Button
                                style={{ background: '#10b981b8', color: '#fff' }}
                                htmlType="button"
                                onClick={() => {
                                    setSaveMode('DRAFT')
                                    saveModeRef.current = 'DRAFT'
                                    handleSubmit()
                                }}
                            >
                                Save as Draft
                            </Button>
                            <Button
                                type="primary"
                                htmlType="button"
                                onClick={() => {
                                    setSaveMode('SAVE')
                                    saveModeRef.current = 'SAVE'
                                    handleSubmit()
                                }}
                            >
                                Activate
                            </Button>
                        </>
                    ) : (
                        <Button
                            type="primary"
                            htmlType="button"
                            onClick={() => {
                                setSaveMode('SAVE')
                                saveModeRef.current = 'SAVE'
                                handleSubmit()
                            }}
                        >
                            Save
                        </Button>
                    )}
                </div>
            </div>
            <Form
                style={{ maxWidth: '1000px' }}
                onFinish={handleSubmit}
                form={form}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 17 }}
            >
                <BasicDetails active={isBasicTab}>
                <FormItem
                    name="name"
                    rules={[
                        { required: true, message: 'Please input Publisher name' },
                        { max: 255, message: 'Name cannot be longer that 255 characters' }
                    ]}
                    label="Name"
                    tooltip={'Name of the publisher'}
                >
                    <Input />
                </FormItem>
                <FormItem
                    name="sellerAccountId"
                    rules={[{ type: 'integer', min: 1, max: 9999, message: 'Please input a valid id from 1 to 9999' }]}
                    label="SellerAccountId"
                    tooltip={'SellerAccountId of the publisher'}
                >
                    <InputNumber style={{ width: '100%' }} controls={false} disabled />
                </FormItem>
                <FormItem
                    name="language"
                    label="Language"
                    tooltip={'Publisher language'}
                    rules={[{ max: 255, message: 'Language cannot be longer that 255 characters' }]}
                >
                    <Select optionFilterProp="title" showSearch allowClear placeholder="Select language">
                        {info.languages.map((item: LanguageBean) => (
                            <Option title={item.name} key={item.id} value={item.id as string}>{item.name}</Option>))}
                    </Select>
                </FormItem>
                </BasicDetails>

                <FilteringOptions active={isFilteringTab}>
                <FormItem
                    name="maxQps"
                    label="QPS"
                    tooltip={'Maximum queries per second allowed'}
                    rules={[{ pattern: /^\d*$/, message: 'Please input a valid number' }]}
                >
                    <Input />
                </FormItem>
                {/* <FormItem
                    name="blackListDomainsField"
                    label="Filter Mode"
                    initialValue="white"
                    tooltip={'Choose Domain filter mode'}
                >
                    <Segmented
                        options={[
                            {
                                label: 'Allow list',
                                value: 'white',
                                icon: <CheckCircleOutlined />
                            },
                        ]}
                    />
                </FormItem> */}
                <FormItem
                    label="Allowed Domains"
                    tooltip={'Publisher allowed domains with classifications'}
                    validateStatus={domainMobileError ? 'error' : undefined}
                    help={domainMobileError || undefined}
                >
                    <PublisherDomainListCard
                        title="Web Domains"
                        subtitle="Manage allowlist entries"
                        items={domains}
                        acceptAll={allClassification}
                        collapsed={domainsCollapsed}
                        onToggleCollapse={() => setDomainsCollapsed(v => !v)}
                        onAcceptAllChange={checked => {
                            setAllClassification(checked)
                            if (checked) setDomains([])
                            setDomainMobileError('')
                        }}
                        onAdd={handleAddDomain}
                        onUpload={handleUploadDomains}
                        onRemoveItem={handleRemoveDomain}
                        onClearAll={handleClearAllDomains}
                        initialFileLabel={domainsFileLabel}
                    />
                </FormItem>

                <FormItem
                    name="includeDomains"
                    label="Include Allowed Domains in sellers.JSON"
                    valuePropName="checked"
                >
                    <Checkbox checked={includeDomains} onChange={handleIncludeDomains} />
                </FormItem>

                <FormItem
                    label="Allowed Mobile App IDs"
                    tooltip={'Publisher allowed mobile app IDs with classifications'}
                    validateStatus={domainMobileError ? 'error' : undefined}
                    help={domainMobileError || undefined}
                >
                    <PublisherDomainListCard
                        title="MobileApp ID"
                        subtitle="Manage allowlist entries"
                        items={mobileAppIds}
                        acceptAll={acceptAllMobileAppIds}
                        collapsed={mobileAppsCollapsed}
                        onToggleCollapse={() => setMobileAppsCollapsed(v => !v)}
                        onAcceptAllChange={checked => {
                            setAcceptAllMobileAppIds(checked)
                            if (checked) setMobileAppIds([])
                            setDomainMobileError('')
                        }}
                        onAdd={handleAddMobileAppId}
                        onUpload={handleUploadMobileAppIds}
                        onRemoveItem={handleRemoveMobileAppId}
                        onClearAll={handleClearAllMobileAppIds}
                        initialFileLabel={mobileAppIdsFileLabel}
                    />
                </FormItem>
                <FormItem
                    name="iabCategoriesIds"
                    label="Publisher IAB Categories"
                    tooltip={'IAB categories publisher wants to work with'}
                >
                    <TreeSelect
                        allowClear
                        treeCheckable
                        showCheckedStrategy="SHOW_ALL"
                        style={{ maxHeight: '400px', overflow: 'auto' }}
                        placement={'topRight'}
                        placeholder="Select IAB categories"
                        onChange={onChangeIab}
                        treeData={getIabCategoriesAsTree(info.iabCategories)}
                    />
                </FormItem>
                <FormItem
                    name="dspBlackListIds"
                    label="Dsp blacklist"
                    getValueFromEvent={handleSelectAll}
                    tooltip={'Dsps to do not get ads from'}
                >
                    <Select showSearch allowClear mode="multiple" placeholder="Select dsp">
                        {info.dspList.length > 0 && <Option value={'__all'}>{'Select all dsps'}</Option>}
                        {info.dspList.map((item: DspBean) => (
                            <Option key={item.id} value={item.id as number}>{item.name}</Option>
                        ))}
                    </Select>
                </FormItem>
                </FilteringOptions>

                <PaymentDetails active={isPaymentTab}>
                {/* <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <Button type="primary" style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }} onClick={handleOpenCustomFieldModal}>
                        + Add a field
                    </Button>
                </div> */}
                <FormItem name="adfiEligible" label="AdFI Eligible" valuePropName="checked">
                    <Switch onChange={handleAdfiEligibleChange} disabled={adfiEligible} />
                </FormItem>
                <FormItem name="adfiFeePercent" label="AdFI Fee %" rules={getAdfiRequiredRule('AdFI Fee %')}>
                    <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="serviceFeePercent" label="Service Fee %" rules={getAdfiRequiredRule('Service Fee %')}>
                    <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="publisherLegalName" label="Publisher Legal Name" rules={getAdfiRequiredRule('Publisher Legal Name')}>
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="publisherRegisteredAddress" label="Publisher Registered Address" rules={getAdfiRequiredRule('Publisher Registered Address')}>
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem
                    name="publisherContactEmail"
                    label="Publisher Contact Email"
                    rules={[...getAdfiRequiredRule('Publisher Contact Email'), { pattern: new RegExp('^$|.+@.+\\..+'), message: 'Email is incorrect' }]}
                >
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="currencyPreference" label="Currency Preference" rules={getAdfiRequiredRule('Currency Preference')}>
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="vatRate" label="VAT Rate" rules={getAdfiRequiredRule('VAT Rate')}>
                    <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="vatRegistrationNumber" label="VAT Registration Number">
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="bankAccountName" label="Bank Account Name" rules={getAdfiRequiredRule('Bank Account Name')}>
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="bankAccountType" label="Bank Account Type" rules={adfiEligible ? [{ required: true, message: 'Please select Bank Account Type' }] : []}>
                    <Radio.Group disabled={!adfiEligible}>
                        <Radio value="CHECKING">Checking</Radio>
                        <Radio value="SAVINGS">Savings</Radio>
                    </Radio.Group>
                </FormItem>
                <FormItem name="bankAccountNumber" label="Bank Account Number" rules={getAdfiRequiredRule('Bank Account Number')}>
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="sortCode" label="Sort Code" rules={getAdfiRequiredRule('Sort Code')}>
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="iban" label="IBAN" rules={getAdfiRequiredRule('IBAN')}>
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="swiftBicCode" label="Swift / BIC Code" rules={getAdfiRequiredRule('Swift / BIC Code')}>
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="routingNumber" label="Routing Number" rules={getAdfiRequiredRule('Routing Number')}>
                    <Input disabled={!adfiEligible} />
                </FormItem>
                <FormItem name="paymentTermsDays" label="Payment Terms (Days)" rules={getAdfiRequiredRule('Payment Terms (Days)')}>
                    <InputNumber style={{ width: '100%' }} min={0} disabled={!adfiEligible} />
                </FormItem>
                {customPaymentFields.map((field) => (
                    <FormItem key={field.id} name={['customPaymentFields', field.id]} label={field.label}>
                        <Input
                            disabled={!adfiEligible}
                            placeholder={field.placeholder || undefined}
                            addonAfter={
                                <Button type="link" danger style={{ padding: 0, height: 'auto' }} onClick={() => handleRemoveCustomField(field.id)}>
                                    Remove
                                </Button>
                            }
                        />
                    </FormItem>
                ))}
                </PaymentDetails>

                <BasicDetails active={isBasicTab}>
                <FormItem name="winNotification" label="Win notification" initialValue="Nurl">
                    <Select allowClear placeholder="Select win notification type">
                        {Object.values(WinNotificationEnum).map(win => <Option key={win} value={win}>{win}</Option>)}
                    </Select>
                </FormItem>
                <FormItem name="bundle" rules={[{ max: 255, message: 'Bundle cannot be longer that 255 characters' }]} label="Bundle" tooltip={'Publisher\'s App bundle'}>
                    <Input />
                </FormItem>
                <FormItem name="storeUrl" rules={[{ max: 255, message: 'Store URL cannot be longer that 255 characters' }]} label="Store URL" tooltip={'Publisher\'s App store URL'}>
                    <Input />
                </FormItem>
                {/* <FormItem name="publisherDomain" ... /> */}
                <FormItem name="auctionMode" label="Auction Mode" rules={[{ required: true, message: 'Please select Auction type' }]} tooltip={'Auction type, where 1 = First Price, 2 = Second Price Plus. Exchange-specific auction types can be defined using values greater than 500'}>
                    <Select placeholder="Select Auction Mode">
                        {Object.keys(PublisherBean.AuctionModeEnum).map((mode) => <Option key={mode} value={mode}>{mode.replace(/([a-z])([A-Z])/g, '$1 $2')}</Option>)}
                    </Select>
                </FormItem>
                <FormItem name="houseBidderMode" label="House Bidder Mode" rules={[{ required: true, message: 'Please select House Bidder Mode' }]} tooltip={'House Bidder Mode'}>
                    <Select placeholder="Select House Bidder Mode">
                        {Object.keys(PublisherBean.HouseBidderModeEnum).map((mode) => <Option key={mode} value={mode}>{mode.replace(/([a-z])([A-Z])/g, '$1 $2')}</Option>)}
                    </Select>
                </FormItem>
                <FormItem label="Token" tooltip={'Token identifying publisher on the exchage. Must be inserted in html page'} rules={[{ max: 255, message: 'Token cannot be longer that 255 characters' }]}>
                    <Input value={token} addonAfter={(
                        <Button style={{ border: 0, backgroundColor: '#fafafa' }} size={'small'} htmlType="button" onClick={handleTokenGenerator}>Generate</Button>
                    )} />
                </FormItem>
                <FormItem required label="Alkimi Exchange Fee" tooltip="Publisher Fee">
                    <FormItem name="fee" rules={[{ required: true, message: 'Fee is required' }]} className="ant-item ant-item-first">
                        <InputNumber step={0.01} min={0} max={99} />
                    </FormItem>
                    <FormItem className="ant-item ant-item-second">%</FormItem>
                </FormItem>
                <FormItem name="sellerDomain" label="Seller Domain" rules={[{ required: true, message: 'Please input Seller Domain' }]} tooltip={'Seller domains (domain1)'}>
                    <Input />
                </FormItem>
                <FormItem name="sellerType" label="Seller Type" rules={[{ required: true, message: 'Please select Seller type' }]}>
                    <Select placeholder="Select Seller type">
                        {SellerType.map(element => <Option key={element.value} value={element.value}>{element.label}</Option>)}
                    </Select>
                </FormItem>
                <FormItem name="revenueCalculationMethod" label="Revenue Calculation Method" rules={[{ required: true, message: 'Please select Revenue Calculation Method' }]}>
                    <Select placeholder="Select Revenue Calculation Method">
                        {RevenueCalculationMethod.map(element => <Option key={element.value} value={element.value}>{element.label}</Option>)}
                    </Select>
                </FormItem>
                <FormItem name="passThrough" label="Pass Through" valuePropName="checked">
                    <Checkbox onChange={handlepassThrough} />
                </FormItem>
                <FormItem name="useParentSellerAccountId" label="Use Parent Seller Id" valuePropName="checked">
                    <Checkbox onChange={handleuseParentSellerAccountId} />
                </FormItem>
                <FormItem
                    name="parentPublisherId"
                    label="Parent Publisher"
                    rules={useParentSellerAccountId ? [{ required: true, message: 'Please select parent publisher id' }] : []}
                    tooltip={'Select Parent Publisher Id'}
                >
                    <Select showSearch allowClear onChange={handleParentPublisherId} placeholder="Select parent publisher id" filterOption={(input, option) => option?.children?.toLowerCase()?.includes(input?.toLowerCase())}>
                        {info?.publishers?.map((item: DspBean) => {
                            return item?.id != id ? <Option key={item.id} value={item.id as number}>{item.name}</Option> : null
                        })}
                    </Select>
                </FormItem>
                <FormItem name="validation" label="Validation" valuePropName="checked" tooltip={'If true publisher should send signed request only'}>
                    <Checkbox onChange={handleValidation} />
                </FormItem>
                <FormItem name="publicValidationKey" label="Public validation key" rules={[{ max: 255, message: 'Public validation key cannot be longer that 255 characters' }]} tooltip={'Public validation key need to validate publisher request'}>
                    <Input disabled={!validation} />
                </FormItem>
                {/* <FormItem name="active" ... /> */}
                <FormItem name="email" rules={[{ required: true, message: 'Please input Publisher email' }, { pattern: new RegExp('.+@.+\\..+'), message: 'Email is incorrect' }]} label="Email">
                    <Input />
                </FormItem>
                <FormItem name="timezoneId" rules={[{ required: true, message: 'Please select Time Zone', type: 'string' }]} label="Time Zone">
                    <Select showSearch placeholder="Select time zone">
                        {info.timezones.map((timezone: TimeZoneBean) => <Option key={timezone.id} value={timezone.id as string}>{timezone.id}</Option>)}
                    </Select>
                </FormItem>
                <FormItem name="test" label="Restrict Blockchain Transaction" valuePropName="checked" tooltip="If true publisher transactions will not be sent to Blockchain ledger">
                    <Checkbox />
                </FormItem>
                <FormItem required label="Validations limit" tooltip="Limit the number of validation report">
                    <FormItem name="athenaValidationsLimit" rules={[{ required: true, message: 'Limit is required' }]} className="ant-item ant-item-first">
                        <InputNumber min={0} defaultValue={0} />
                    </FormItem>
                    <FormItem className="ant-item ant-item-second">Validations remaining in the current period: {form.getFieldValue('athenaValidationsLeft')}</FormItem>
                </FormItem>
                <FormItem required label="Request/Response Id period limit" tooltip="Limit of period for Request/Response Id report in hours">
                    <FormItem name="athenaPeriodLimit" rules={[{ required: true, message: 'Limit is required' }]} className="ant-item ant-item-first">
                        <InputNumber min={0} defaultValue={0} />
                    </FormItem>
                    <FormItem className="ant-item ant-item-second">Report period remaining in the current period: {form.getFieldValue('athenaPeriodLeft')}</FormItem>
                    <FormItem className="ant-item ant-item-second">hours</FormItem>
                </FormItem>
                </BasicDetails>

                <FilteringOptions active={isFilteringTab}>
                <FormItem name="countries" label="Countries" tooltip="Countries">
                    <Select mode="multiple" showSearch allowClear placeholder="Select Countries" optionFilterProp="title">
                        {info.countries.map((country: CountryBean) => (
                            <Option key={country.id} title={country.name} value={country.id as number}>{country.name}</Option>
                        ))}
                    </Select>
                </FormItem>
                </FilteringOptions>
            </Form>

            <Modal
                title="Add Custom Field"
                open={isCustomFieldModalOpen}
                onCancel={() => setIsCustomFieldModalOpen(false)}
                onOk={handleAddCustomField}
                okText="Add Field"
                destroyOnClose
            >
                <Form layout="vertical" form={customFieldForm}>
                    <FormItem name="fieldLabel" label="Field Label" rules={[{ required: true, message: 'Please input field label' }]}>
                        <Input placeholder="e.g Sort Code" />
                    </FormItem>
                    <FormItem name="placeholder" label="Placeholder / Hint (optional)">
                        <Input placeholder="e.g Enter your Sort Code" />
                    </FormItem>
                </Form>
            </Modal>
        </div>
    )
}

export default PublisherEdit